import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from './components/Avatar/Avatar';
import { ChatWidget } from './components/Chat/ChatWidget';
import { BookingForm } from './components/Booking/BookingForm';
import { LanguageSelector } from './components/LanguageSelector/LanguageSelector';
import { apiClient } from './services/api';
import { chatService } from './services/chat.service';
import { useAppStore } from './store/appStore';
import './App.css';

function App() {
  const { t, i18n } = useTranslation();
  const [languageSelected, setLanguageSelected] = useState<boolean>(
    () => !!localStorage.getItem('selectedLanguage')
  );
  const greetedRef = useRef(false);
  const audioPlayedRef = useRef(false);
  const prevViewModeRef = useRef<'chat' | 'form' | 'initial'>('initial');
  const clearMessages = useAppStore((state) => state.clearMessages);
  const setAvatarSpeaking = useAppStore((state) => state.setAvatarSpeaking);
  const setCurrentVisemes = useAppStore((state) => state.setCurrentVisemes);
  const setAudioElement = useAppStore((state) => state.setAudioElement);
  const viewMode = useAppStore((state) => state.viewMode);
  const setViewMode = useAppStore((state) => state.setViewMode);

  const handleLanguageSelect = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('selectedLanguage', lang);
    setLanguageSelected(true);
  };

  const handleLogoClick = () => {
    // Stop any playing audio
    const audioElement = useAppStore.getState().audioElement;
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
      setAudioElement(null);
    }
    setAvatarSpeaking(false);
    setCurrentVisemes([]);

    // Clear language selection and return to language selector
    localStorage.removeItem('selectedLanguage');
    setLanguageSelected(false);
    // Reset greeted flag
    greetedRef.current = false;
    audioPlayedRef.current = false;
  };

  const handleHeaderLanguageChange = (lang: string) => {
    console.log('[App] 🌐 Language change initiated to:', lang);

    // CRITICAL: Save current viewMode to restore after reload
    const currentViewMode = useAppStore.getState().viewMode;
    sessionStorage.setItem('previousViewMode', currentViewMode);
    console.log('[App] 💾 Saved viewMode for restore:', currentViewMode);

    // CRITICAL: Set flag in sessionStorage to persist through reload
    sessionStorage.setItem('isChangingLanguage', 'true');
    sessionStorage.setItem('languageChangeTimestamp', Date.now().toString());

    // CRITICAL: Set flag to prevent any new audio from starting
    useAppStore.getState().setIsChangingLanguage(true);

    // CRITICAL: Stop ALL audio immediately before language change
    const audioElement = useAppStore.getState().audioElement;
    if (audioElement) {
      console.log('[App] 🔇 Stopping audio before language change');
      audioElement.pause();
      audioElement.currentTime = 0;
      audioElement.src = ''; // Clear source to prevent further playback
    }
    setAudioElement(null);
    setAvatarSpeaking(false);
    setCurrentVisemes([]);

    // Reset flags
    greetedRef.current = false;
    audioPlayedRef.current = false;

    // Reset store state for instructions
    useAppStore.getState().setInstructionsSpoken(false);
    useAppStore.getState().setLastSpokenFormStep(-1);

    // Change language and store
    i18n.changeLanguage(lang);
    localStorage.setItem('selectedLanguage', lang);

    console.log('[App] 🔄 Reloading page...');
    // Small delay to ensure audio is stopped, then reload
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  useEffect(() => {
    // Check if we're recovering from a language change (persists through reload)
    const isChangingLanguage = sessionStorage.getItem('isChangingLanguage');
    const changeTimestamp = sessionStorage.getItem('languageChangeTimestamp');
    const previousViewMode = sessionStorage.getItem('previousViewMode');

    if (isChangingLanguage === 'true') {
      console.log('[App] 🔄 Detected language change flag from previous session');

      // Set the flag in store to prevent audio
      useAppStore.getState().setIsChangingLanguage(true);

      // Check if the change is recent (within last 5 seconds)
      const timestamp = parseInt(changeTimestamp || '0');
      const timeDiff = Date.now() - timestamp;

      if (timeDiff < 5000) {
        console.log('[App] ⏱️ Recent language change, blocking audio for 2 seconds');

        // CRITICAL: Restore previous viewMode if user was in chat or form
        if (previousViewMode && (previousViewMode === 'chat' || previousViewMode === 'form')) {
          console.log('[App] 🔙 Restoring previous viewMode:', previousViewMode);
          setViewMode(previousViewMode as 'chat' | 'form');

          // Mark instructions as already spoken to prevent them from playing
          useAppStore.getState().setInstructionsSpoken(true);
        }

        // Keep the flag for 2 seconds to prevent audio on reload
        setTimeout(() => {
          console.log('[App] ✅ Clearing language change flag');
          sessionStorage.removeItem('isChangingLanguage');
          sessionStorage.removeItem('languageChangeTimestamp');
          sessionStorage.removeItem('previousViewMode');
          useAppStore.getState().setIsChangingLanguage(false);
        }, 2000);
      } else {
        // Old flag, clear it immediately
        console.log('[App] 🧹 Old language change flag, clearing immediately');
        sessionStorage.removeItem('isChangingLanguage');
        sessionStorage.removeItem('languageChangeTimestamp');
        sessionStorage.removeItem('previousViewMode');
        useAppStore.getState().setIsChangingLanguage(false);
      }
    }

    // Check backend health on mount
    const checkHealth = async () => {
      try {
        const health = await apiClient.health();
        console.log('Backend health:', health);
      } catch (error) {
        console.error('Backend connection error:', error);
      }
    };

    checkHealth();
  }, []);

  // Stop audio and reset when switching between modes
  useEffect(() => {
    const prevMode = prevViewModeRef.current;

    // Only stop audio and reset when switching between chat/form (not from initial)
    if (prevMode !== 'initial' && viewMode !== 'initial' && prevMode !== viewMode) {
      const audioElement = useAppStore.getState().audioElement;
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
        setAudioElement(null);
      }
      setAvatarSpeaking(false);
      setCurrentVisemes([]);

      // Reset based on mode
      if (viewMode === 'form') {
        // Switching to form: clear chat and reset form step tracker
        clearMessages();
        chatService.clearHistory();
        useAppStore.getState().setLastSpokenFormStep(-1);
      } else if (viewMode === 'chat') {
        // Switching to chat: clear chat and reset form step tracker
        clearMessages();
        chatService.clearHistory();
        useAppStore.getState().setLastSpokenFormStep(-1);
      }
    }

    // Update previous mode
    prevViewModeRef.current = viewMode;
  }, [viewMode]);

  // Greeting is now handled by LanguageSelector, no need for greeting here

  if (!languageSelected) {
    return <LanguageSelector onLanguageSelect={handleLanguageSelect} />;
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <div className="logo-section" onClick={handleLogoClick} style={{ cursor: 'pointer' }} title="Torna alla selezione lingua">
            <img
              src="https://www.caladeisardi.it/wp-content/uploads/2022/10/logo_CDS-1.png"
              alt="Cala dei Sardi Logo"
              className="logo"
            />
          </div>
          <h1>{t('header.title')}</h1>

          <div className="language-switcher">
            <button onClick={() => handleHeaderLanguageChange('it')} className="lang-flag" title="Italiano">
              <img src="https://flagcdn.com/w40/it.png" alt="Italiano" />
            </button>
            <button onClick={() => handleHeaderLanguageChange('en')} className="lang-flag" title="English">
              <img src="https://flagcdn.com/w40/gb.png" alt="English" />
            </button>
            <button onClick={() => handleHeaderLanguageChange('fr')} className="lang-flag" title="Français">
              <img src="https://flagcdn.com/w40/fr.png" alt="Français" />
            </button>
            <button onClick={() => handleHeaderLanguageChange('de')} className="lang-flag" title="Deutsch">
              <img src="https://flagcdn.com/w40/de.png" alt="Deutsch" />
            </button>
            <button onClick={() => handleHeaderLanguageChange('es')} className="lang-flag" title="Español">
              <img src="https://flagcdn.com/w40/es.png" alt="Español" />
            </button>
            <button onClick={() => handleHeaderLanguageChange('pt')} className="lang-flag" title="Português">
              <img src="https://flagcdn.com/w40/pt.png" alt="Português" />
            </button>
            <button onClick={() => handleHeaderLanguageChange('nl')} className="lang-flag" title="Nederlands">
              <img src="https://flagcdn.com/w40/nl.png" alt="Nederlands" />
            </button>
            <button onClick={() => handleHeaderLanguageChange('pl')} className="lang-flag" title="Polski">
              <img src="https://flagcdn.com/w40/pl.png" alt="Polski" />
            </button>
            <button onClick={() => handleHeaderLanguageChange('ru')} className="lang-flag" title="Русский">
              <img src="https://flagcdn.com/w40/ru.png" alt="Русский" />
            </button>
            <button onClick={() => handleHeaderLanguageChange('zh')} className="lang-flag" title="中文">
              <img src="https://flagcdn.com/w40/cn.png" alt="中文" />
            </button>
            <button onClick={() => handleHeaderLanguageChange('ja')} className="lang-flag" title="日本語">
              <img src="https://flagcdn.com/w40/jp.png" alt="日本語" />
            </button>
            <button onClick={() => handleHeaderLanguageChange('ar')} className="lang-flag" title="العربية">
              <img src="https://flagcdn.com/w40/sa.png" alt="العربية" />
            </button>
          </div>
        </div>
      </header>

      <div className={`app-container ${viewMode === 'chat' ? 'chat-mode' : viewMode === 'form' ? 'form-mode' : ''}`}>
        {/* Colonna sinistra: Avatar sempre visibile */}
        <div className="left-column">
          <div className="avatar-section">
            <Avatar />
          </div>
        </div>

        {/* Colonna destra: ChatWidget in modalità chat, BookingForm altrimenti */}
        <div className="right-column">
          {viewMode === 'chat' ? (
            <div className="chat-section-right">
              <ChatWidget />
            </div>
          ) : (
            <BookingForm />
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
